// Reports for POS-connected venues — the analytics a multi-location operator
// actually reads, computed from the live Toast feed, the costed margins, and
// the invoice book. All windows are COMPLETE business days; today never
// pollutes a "last N days" claim.
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { invoicesApi } from '../services/api';

const money = (c: number) => `$${(c / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const money2 = (c: number) => `$${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const nyDate = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }).replace(/-/g, '');
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ---------- Item lookup ----------
// A searchable, per-item sales card. Self-contained and POS-only (no OpenAI):
// it queries /item-lookup directly, so it works even when the signed-in store
// (e.g. a commissary hub) has no POS feed of its own but its stores do.
const PERIODS: { key: string; label: string }[] = [
  { key: 'last30', label: 'Last 30 days' },
  { key: 'thisWeek', label: 'This week' },
  { key: 'thisMonth', label: 'This month' },
];

export function ItemLookupSection() {
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [period, setPeriod] = useState('last30');
  const [scope, setScope] = useState('all');

  // The account's stores (for the scope picker). Owners get the list; a single
  // store just searches itself.
  const { data: venuesOv } = useQuery({
    queryKey: ['owner-overview'],
    queryFn: async () => (await api.get('/v1/owner/overview')).data?.data,
    staleTime: 60_000,
  });
  const venues: any[] = venuesOv?.venues || [];
  const shortName = (n: string) => (n.includes('—') ? n.split('— ')[1].trim() : n);

  const { data: result, isFetching } = useQuery({
    queryKey: ['item-lookup', query, period, scope],
    enabled: query.trim().length > 0,
    queryFn: async () => (await api.get('/v1/integrations/toast/item-lookup', {
      params: { q: query, period, restaurantId: scope },
    })).data?.data,
  });

  const submit = () => setQuery(draft.trim());
  const it = result?.item;
  const maxTrend = Math.max(1, ...((it?.trend || []).map((d: any) => d.grossRevenueCents || 0)));

  return (
    <div className="card p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <p className="text-xs uppercase tracking-wide text-slate-400">Item lookup</p>
        <div className="flex items-center gap-2">
          {venues.length > 1 && (
            <select className="input input-sm w-40" value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="all">All stores</option>
              {venues.map((v) => <option key={v.id} value={v.id}>{shortName(v.name)}</option>)}
            </select>
          )}
          <select className="input input-sm w-36" value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          className="input flex-1"
          placeholder="Search a menu item — e.g. everything bagel, cold brew"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />
        <button className="btn btn-primary" onClick={submit} disabled={!draft.trim()}>Look up</button>
      </div>

      {/* states */}
      {query && isFetching && <p className="text-sm text-slate-400 mt-4">Looking up “{query}”…</p>}

      {query && !isFetching && result && !result.connected && (
        <p className="text-sm text-slate-500 mt-4">No POS sales are synced for this scope yet — connect Toast or Square to search items.</p>
      )}

      {query && !isFetching && result && result.connected && result.noMatch && (
        <p className="text-sm text-slate-500 mt-4">Nothing matching “{query}” sold in this window. Try another spelling or a broader term.</p>
      )}

      {query && !isFetching && it && (
        <div className="mt-4 space-y-4">
          {/* headline figures */}
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
            <div>
              <p className="text-lg font-semibold text-slate-900">{it.name}</p>
              {it.variants?.length > 1 && (
                <p className="text-xs text-slate-400">{it.variants.length} name variants folded in</p>
              )}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span><span className="font-semibold tabular-nums text-slate-900">{it.unitsSold.toLocaleString('en-US')}</span> <span className="text-slate-400">sold</span></span>
              <span><span className="font-semibold tabular-nums text-slate-900">{money2(it.grossRevenueCents)}</span> <span className="text-slate-400">revenue</span></span>
              <span><span className="font-semibold tabular-nums text-slate-900">{money2(it.avgPriceCents)}</span> <span className="text-slate-400">avg price</span></span>
              <span>
                {it.marginPct !== null
                  ? <><span className="font-semibold tabular-nums text-emerald-700">{it.marginPct}%</span> <span className="text-slate-400">est. margin</span></>
                  : <span className="text-slate-400">no recipe — add one to see margin</span>}
              </span>
            </div>
          </div>

          {/* trend sparkline (revenue by day) */}
          {it.trend?.length > 0 && (
            <div>
              <p className="text-xs text-slate-400 mb-1">Daily units · {result.scope?.start} → {result.scope?.end}</p>
              <div className="flex items-end gap-0.5 h-20">
                {it.trend.map((d: any) => (
                  <div key={d.date} className="flex-1 bg-primary-500/80 rounded-sm hover:bg-primary-600 transition-colors"
                    title={`${d.date}: ${d.unitsSold} sold · ${money2(d.grossRevenueCents)}`}
                    style={{ height: `${Math.max(2, Math.round(((d.grossRevenueCents || 0) / maxTrend) * 100))}%` }} />
                ))}
              </div>
            </div>
          )}

          {/* per-store breakdown */}
          {it.byStore?.length > 1 && (
            <div>
              <p className="text-xs text-slate-400 mb-1">By store</p>
              <div className="space-y-1">
                {it.byStore.map((s: any) => (
                  <div key={s.restaurantId} className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="text-slate-700 truncate">{s.storeName}</span>
                    <span className="tabular-nums text-slate-500 whitespace-nowrap">
                      {s.unitsSold.toLocaleString('en-US')} · <span className="font-medium text-slate-900">{money2(s.grossRevenueCents)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* candidates */}
          {result.candidates?.length > 0 && (
            <p className="text-xs text-slate-400">
              Other matches:{' '}
              {result.candidates.map((c: any, i: number) => (
                <span key={c.name}>
                  <button className="text-primary-600 hover:underline" onClick={() => { setDraft(c.name); setQuery(c.name); }}>{c.name}</button>
                  {i < result.candidates.length - 1 ? ', ' : ''}
                </span>
              ))}
            </p>
          )}

          {/* provenance */}
          {it.provenance?.length > 0 && (
            <p className="text-xs text-slate-400 border-t border-slate-100 pt-2">{it.provenance.join(' · ')}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ToastReportsPage() {
  const { data: status } = useQuery({
    queryKey: ['toast-status'],
    queryFn: async () => (await api.get('/v1/integrations/toast/status')).data?.data,
  });
  const { data: margins } = useQuery({
    queryKey: ['toast-margins'],
    queryFn: async () => (await api.get('/v1/integrations/toast/margins?days=28')).data?.data,
  });
  const { data: forecast } = useQuery({
    queryKey: ['toast-forecast'],
    queryFn: async () => (await api.get('/v1/integrations/toast/forecast')).data?.data,
  });
  const { data: invoicesData } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const response = await invoicesApi.getAll({});
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  const { data: insight } = useQuery({
    queryKey: ['insight-days'],
    queryFn: async () => (await api.get('/v1/integrations/toast/insight-days')).data?.data,
  });

  const r = useMemo(() => {
    const all: any[] = (status?.salesSummary?.days?.length ? status.salesSummary.days : insight?.days) || [];
    const today = nyDate();
    const days = all.filter((d) => String(d.businessDate) !== today)
      .sort((a, b) => String(b.businessDate).localeCompare(String(a.businessDate)));
    const last30 = days.slice(0, 30);
    const prior30 = days.slice(30, 60);
    const sum = (rows: any[]) => rows.reduce((s, d) => s + (d.grossCents || 0), 0);
    const orders = (rows: any[]) => rows.reduce((s, d) => s + (d.orders || 0), 0);
    const gross30 = sum(last30); const orders30 = orders(last30);
    const grossPrior = sum(prior30);
    const trendPct = grossPrior > 0 ? Math.round(((gross30 - grossPrior) / grossPrior) * 100) : null;

    // by weekday (average of each weekday's complete days)
    const wd: Record<number, { total: number; n: number }> = {};
    for (const d of last30) {
      const bd = String(d.businessDate);
      const dt = new Date(`${bd.slice(0, 4)}-${bd.slice(4, 6)}-${bd.slice(6, 8)}T12:00:00`);
      const k = (dt.getDay() + 6) % 7; // Mon=0
      wd[k] = wd[k] || { total: 0, n: 0 };
      wd[k].total += d.grossCents || 0; wd[k].n += 1;
    }
    const weekday = WEEKDAYS.map((label, i) => ({ label, avg: wd[i]?.n ? Math.round(wd[i].total / wd[i].n) : 0 }));
    const maxWd = Math.max(1, ...weekday.map((w) => w.avg));

    const trend = [...last30].reverse();
    const maxDay = Math.max(1, ...trend.map((d) => d.grossCents || 0));

    // product mix + add-ons over the last 7 complete days
    const last7 = days.slice(0, 7);
    const mix: Record<string, { qty: number; gross: number }> = {};
    const addons: Record<string, { qty: number; gross: number }> = {};
    for (const d of last7) for (const [name, v] of Object.entries((d.items || {}) as Record<string, any>)) {
      const bucket = name.startsWith('+ ') ? addons : mix;
      const t = bucket[name] || { qty: 0, gross: 0 };
      t.qty += v.quantity || 0; t.gross += v.grossCents || 0; bucket[name] = t;
    }
    const mixTotal = Object.values(mix).reduce((s, v) => s + v.gross, 0);
    const topMix = Object.entries(mix).sort((a, b) => b[1].gross - a[1].gross).slice(0, 12);
    const topAddons = Object.entries(addons).sort((a, b) => b[1].qty - a[1].qty).slice(0, 8);

    return { days, gross30, orders30, trendPct, weekday, maxWd, trend, maxDay, topMix, mixTotal, topAddons, n30: last30.length };
  }, [status, insight]);

  const fc = useMemo(() => {
    const rows: any[] = (margins?.rows || []).filter((x: any) => typeof x.costCents === 'number' && x.costCents > 0);
    let rev = 0, cost = 0;
    for (const x of rows) { rev += (x.avgPriceCents || 0) * (x.soldPerWeek || 0); cost += (x.costCents || 0) * (x.soldPerWeek || 0); }
    const foodCostPct = rev > 0 ? Math.round((cost / rev) * 100) : null;
    const byProfit = [...rows].map((x) => ({ ...x, weekProfit: Math.round(((x.avgPriceCents || 0) - (x.costCents || 0)) * (x.soldPerWeek || 0)) }));
    const top = byProfit.sort((a, b) => b.weekProfit - a.weekProfit).slice(0, 5);
    const thin = [...rows].filter((x) => (x.avgPriceCents || 0) > 0)
      .map((x) => ({ ...x, pct: Math.round((1 - (x.costCents || 0) / x.avgPriceCents) * 100) }))
      .sort((a, b) => a.pct - b.pct).slice(0, 5);
    return { foodCostPct, top, thin, costed: rows.length };
  }, [margins]);

  const spend = useMemo(() => {
    const all: any[] = Array.isArray(invoicesData) ? invoicesData : [];
    const d30 = Date.now() - 30 * 86400000;
    const vend = new Map<string, number>();
    let total = 0;
    for (const inv of all) {
      const t = inv.invoiceDate ? new Date(inv.invoiceDate).getTime() : NaN;
      if (Number.isNaN(t) || t < d30) continue;
      total += inv.totalCents || 0;
      const vn = inv.vendor?.name || 'Vendor';
      vend.set(vn, (vend.get(vn) || 0) + (inv.totalCents || 0));
    }
    return { total, vendors: [...vend.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5) };
  }, [invoicesData]);

  const { data: priceVar } = useQuery({
    queryKey: ['price-variation'],
    queryFn: async () => (await api.get('/v1/inventory/price-variation')).data?.data,
  });

  const fdays: any[] = forecast?.days || [];
  const fweek = fdays.reduce((s, d) => s + (d.expectedGrossCents || 0), 0);

  if (!r.days.length) {
    // This store has no POS feed of its own — but the account's other stores
    // might, so the item lookup still works across them.
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500 mt-2">Reports build themselves from your POS history — connect Toast or Square in Settings → Integrations.</p>
        </div>
        <ItemLookupSection />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
        <p className="text-slate-500">Computed from your POS feed, your recipe costs, and your invoice book — net sales, complete days only, as Toast reports them.</p>
      </div>

      {/* item lookup — search a single item's sales story */}
      <ItemLookupSection />

      {/* hero */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400">Sales · last {r.n30} days</p>
          <p className="text-2xl font-semibold text-slate-900 tabular-nums mt-1">{money(r.gross30)}</p>
          {r.trendPct !== null && (
            <p className="text-xs mt-1">
              <span className={r.trendPct >= 0 ? 'text-emerald-700' : 'text-red-700'}>{r.trendPct >= 0 ? '+' : ''}{r.trendPct}%</span>
              <span className="text-slate-400"> vs prior 30</span>
            </p>
          )}
        </div>
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400">Orders</p>
          <p className="text-2xl font-semibold text-slate-900 tabular-nums mt-1">{r.orders30.toLocaleString('en-US')}</p>
          <p className="text-xs text-slate-500 mt-1">avg {money2(r.orders30 ? Math.round(r.gross30 / r.orders30) : 0)} / order</p>
        </div>
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400">Food cost</p>
          <p className="text-2xl font-semibold text-slate-900 tabular-nums mt-1">{fc.foodCostPct !== null ? `${fc.foodCostPct}%` : '—'}</p>
          <p className="text-xs text-slate-500 mt-1">sales mix × recipe costs ({fc.costed} items)</p>
        </div>
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400">Next 7 days · expected</p>
          <p className="text-2xl font-semibold text-slate-900 tabular-nums mt-1">{fweek ? money(fweek) : '—'}</p>
          <p className="text-xs text-slate-500 mt-1">weekday model on your history</p>
        </div>
      </div>

      {/* patterns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Average day by weekday</p>
          <div className="space-y-2">
            {r.weekday.map((w) => (
              <div key={w.label} className="flex items-center gap-3">
                <span className="w-9 text-xs text-slate-500">{w.label}</span>
                <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-2 rounded-full bg-primary-500" style={{ width: `${Math.max(3, Math.round((w.avg / r.maxWd) * 100))}%` }} />
                </div>
                <span className="w-20 text-right text-sm tabular-nums text-slate-900">{w.avg ? money(w.avg) : '—'}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Daily sales · last {r.n30} complete days</p>
          <div className="flex items-end gap-1 h-36">
            {r.trend.map((d) => (
              <div key={d.businessDate} className="flex-1 bg-primary-500/80 rounded-sm hover:bg-primary-600 transition-colors"
                title={`${d.businessDate}: ${money(d.grossCents)} · ${d.orders} orders`}
                style={{ height: `${Math.max(4, Math.round(((d.grossCents || 0) / r.maxDay) * 100))}%` }} />
            ))}
          </div>
        </div>
      </div>

      {/* product mix + add-ons */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Product mix · last 7 complete days</p>
          <div className="space-y-1.5">
            {r.topMix.map(([name, v]: any) => (
              <div key={name} className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-slate-700 truncate">{name}</span>
                <span className="tabular-nums text-slate-500 whitespace-nowrap">
                  {v.qty.toLocaleString('en-US')} · <span className="font-medium text-slate-900">{money(v.gross)}</span>
                  <span className="text-slate-400 ml-1">{r.mixTotal ? Math.round((v.gross / r.mixTotal) * 100) : 0}%</span>
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Add-on attach · last 7 complete days</p>
          {r.topAddons.length === 0 ? (
            <p className="text-sm text-slate-500">No modifier data in this window yet.</p>
          ) : (
            <div className="space-y-1.5">
              {r.topAddons.map(([name, v]: any) => (
                <div key={name} className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="text-slate-700 truncate">{name.slice(2)}</span>
                  <span className="tabular-nums text-slate-500">{v.qty.toLocaleString('en-US')} attaches · <span className="font-medium text-slate-900">{money(v.gross)}</span></span>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">Demand hiding inside other items — it feeds prep and ordering.</p>
        </div>
      </div>

      {/* margins */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Top earners · weekly profit</p>
          <div className="space-y-1.5">
            {fc.top.map((x: any) => (
              <div key={x.toastName || x.name} className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-slate-700 truncate">{x.toastName || x.name}</span>
                <span className="tabular-nums font-medium text-slate-900">{money(x.weekProfit)}<span className="text-slate-400 font-normal">/wk</span></span>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Thinnest margins</p>
          <div className="space-y-1.5">
            {fc.thin.map((x: any) => (
              <div key={x.toastName || x.name} className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-slate-700 truncate">{x.toastName || x.name}</span>
                <span className="tabular-nums text-slate-900">{x.pct}%<span className="text-slate-400 ml-1.5">{money2(x.costCents)} cost / {money2(x.avgPriceCents)}</span></span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">Margin % after recipe cost — candidates for a price look or a portion check.</p>
        </div>
      </div>

      {/* weekly recap — the operator's report card, generated + sent from here */}
      <WeeklyRecapCard />

      <AttachRatesCard />
      <StaffingOutlookCard />

      {/* price dispersion from their own purchase history */}
      {priceVar?.items?.length > 0 && (
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Same item, different price — across your stores</p>
          <p className="text-xs text-slate-400 mb-3">From your own purchase history ({priceVar.source}). Spreads can reflect pack changes as well as pricing — the big ones are worth a call.</p>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-400">
              <th className="py-1">Item</th><th className="py-1 text-right">Best</th><th className="py-1 text-right">Worst</th><th className="py-1 text-right">Spread</th><th className="py-1 text-right">Spend</th><th className="py-1 text-right">If all paid best</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {priceVar.items.slice(0, 9).map((x: any) => (
                <tr key={x.name}>
                  <td className="py-1.5 text-slate-700">{x.name}</td>
                  <td className="py-1.5 text-right tabular-nums text-emerald-700">${x.best}/{x.perUnit} <span className="text-xs text-slate-400">{x.bestLoc}</span></td>
                  <td className="py-1.5 text-right tabular-nums text-red-600">${x.worst}/{x.perUnit} <span className="text-xs text-slate-400">{x.worstLoc}</span></td>
                  <td className="py-1.5 text-right tabular-nums">{x.spreadPct}%</td>
                  <td className="py-1.5 text-right tabular-nums text-slate-500">${(x.totalPurchase || 0).toLocaleString('en-US')}</td>
                  <td className="py-1.5 text-right tabular-nums font-medium text-slate-900">${(x.savingsVsBest || 0).toLocaleString('en-US')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {priceVar.excluded?.length > 0 && (
            <p className="text-xs text-slate-400 mt-2">{priceVar.excluded.join(' and ')} excluded — their R365 unit history mixes per-lb and per-case eras; the direct supplier feed will settle them.</p>
          )}
        </div>
      )}

      {/* purchasing + forecast */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Purchasing · last 30 days</p>
          <p className="text-2xl font-semibold text-slate-900 tabular-nums">{money(spend.total)}</p>
          <div className="space-y-1.5 mt-3">
            {spend.vendors.map(([vn, cents]) => (
              <div key={vn} className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-slate-700 truncate">{vn}</span>
                <span className="tabular-nums font-medium text-slate-900">{money(cents)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-3">Forecast · next 7 days</p>
          <div className="space-y-1.5">
            {fdays.slice(0, 7).map((d: any) => (
              <div key={d.date} className="flex items-baseline justify-between gap-2 text-sm">
                <span className="text-slate-700">{d.weekday} <span className="text-slate-400">{d.date?.slice(5)}</span></span>
                <span className="tabular-nums font-medium text-slate-900">{money(d.expectedGrossCents || 0)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// Generate the weekly report card from the live feed, read it, add a note,
// send it — the e-mail your ops lead used to assemble by hand on Mondays.
function WeeklyRecapCard() {
  const lastMonday = (() => {
    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const dow = (now.getDay() + 6) % 7; // Mon=0
    const d = new Date(now); d.setDate(now.getDate() - dow - 7);
    return d.toISOString().slice(0, 10);
  })();
  const [week, setWeek] = useState(lastMonday);
  const [daysMode, setDaysMode] = useState<1 | 7>(7);
  const [venueId, setVenueId] = useState('');
  const { data: venuesOv } = useQuery({
    queryKey: ['owner-overview'],
    queryFn: async () => (await api.get('/v1/owner/overview')).data?.data,
    staleTime: 60_000,
  });
  const [note, setNote] = useState('');
  const [to, setTo] = useState('');
  const [state, setState] = useState('');
  const qcRecap = useQueryClient();
  const { data: recapSettings } = useQuery({
    queryKey: ['weekly-recap-settings'],
    queryFn: async () => (await api.get('/v1/owner/weekly-recap/settings')).data?.data,
  });
  const saveRecapSettings = useMutation({
    mutationFn: async (v: { autoSend?: boolean; recipients?: string[] }) => api.put('/v1/owner/weekly-recap/settings', v),
    onSuccess: () => qcRecap.invalidateQueries({ queryKey: ['weekly-recap-settings'] }),
  });
  const { data: recap, isFetching } = useQuery({
    queryKey: ['weekly-recap', week, daysMode, venueId],
    queryFn: async () => (await api.get(`/v1/owner/weekly-recap?weekStart=${week}&days=${daysMode}${venueId ? `&venueId=${venueId}` : ''}`)).data?.data,
  });
  const send = async () => {
    setState('sending');
    try {
      const el = document.getElementById('recap-editable');
      await api.post('/v1/owner/weekly-recap/send', { weekStart: week, to: to.trim(), note: note.trim() || undefined, days: daysMode, venueId: venueId || undefined, html: el?.innerHTML || undefined });
      setState(`sent to ${to.trim()}`);
    } catch (e: any) { setState(e?.response?.data?.error?.message || 'send failed'); }
  };
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-slate-400">Weekly report card</p>
          <p className="text-xs text-slate-400">Drafted every Monday from the live feed — review, add a note, send.</p>
        </div>
        <div className="flex items-center gap-2">
          <select className="input input-sm w-40" value={venueId} onChange={(e) => setVenueId(e.target.value)}>
            <option value="">All stores</option>
            {(venuesOv?.venues || []).map((v: any) => <option key={v.id} value={v.id}>{v.name.includes('—') ? v.name.split('— ')[1] : v.name}</option>)}
          </select>
          <select className="input input-sm" value={daysMode} onChange={(e) => setDaysMode(Number(e.target.value) as 1 | 7)}>
            <option value={7}>Weekly</option>
            <option value={1}>Daily</option>
          </select>
          <input type="date" className="input input-sm" value={week} onChange={(e) => setWeek(e.target.value)} />
        </div>
      </div>
      {isFetching ? <p className="text-sm text-slate-400">Building the card…</p> : recap?.html ? (
        <>
          <div id="recap-editable" contentEditable suppressContentEditableWarning className="border border-slate-100 rounded-lg p-3 max-h-96 overflow-y-auto focus:outline-primary-200" dangerouslySetInnerHTML={{ __html: recap.html }} />
          <p className="text-xs text-slate-400 mt-1">Click any number or sentence to edit it — what you see is exactly what sends.</p>
          <div className="flex items-end gap-2 mt-3 flex-wrap">
            <div className="flex-1 min-w-48">
              <label className="block text-xs text-slate-500 mb-1">Note on top (optional)</label>
              <input className="input input-sm w-full" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Great push Midtown —let's talk 3rd Ave Friday." />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Send to</label>
              <input className="input input-sm w-64" value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@company.com" />
            </div>
            <button className="btn btn-sm btn-primary" disabled={!to.includes('@') || state === 'sending'} onClick={send}>
              {state === 'sending' ? 'Sending…' : 'Send report'}
            </button>
          </div>
          {state && state !== 'sending' && <p className="text-xs text-slate-500 mt-1">{state}</p>}
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!recapSettings?.autoSend}
                onChange={(e) => saveRecapSettings.mutate({ autoSend: e.target.checked })} />
              Send this store's card automatically every Monday
            </label>
            {recapSettings?.autoSend && (
              <input className="input input-sm w-80" placeholder="recipients, comma-separated"
                defaultValue={(recapSettings?.recipients || []).join(', ')}
                onBlur={(e) => saveRecapSettings.mutate({ recipients: e.target.value.split(',').map((x) => x.trim()).filter((x) => x.includes('@')) })} />
            )}
            <span className="text-[11px] text-slate-400">a copy always lands in your inbox either way</span>
          </div>
        </>
      ) : <p className="text-sm text-slate-400">No data for that week yet.</p>}
    </div>
  );
}

function AttachRatesCard() {
  const { data } = useQuery({
    queryKey: ['attach-rates'],
    queryFn: async () => (await api.get('/v1/integrations/toast/attach-rates')).data?.data,
  });
  if (!data?.rows?.length) return null;
  return (
    <div className="card p-5">
      <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Add-on attach rates · last {data.windowDays} days</p>
      <p className="text-xs text-slate-400 mb-3">How often each add-on rides along, per 100 orders — the menu's quiet upsell scoreboard.</p>
      <div className="space-y-1.5">
        {data.rows.map((r: any) => (
          <div key={r.name} className="flex items-center gap-3">
            <span className="text-sm text-slate-700 w-48 truncate">{r.name}</span>
            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
              <div className="bg-primary-400 h-2 rounded-full" style={{ width: `${Math.min(100, r.ratePct)}%` }} />
            </div>
            <span className="text-sm tabular-nums text-slate-900 font-medium w-14 text-right">{r.ratePct}%</span>
            <span className="text-xs tabular-nums text-slate-400 w-16 text-right">{r.qty.toLocaleString('en-US')} sold</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StaffingOutlookCard() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['staffing-outlook'],
    queryFn: async () => (await api.get('/v1/inventory/staffing-outlook')).data?.data,
  });
  const [editing, setEditing] = useState(false);
  const [roleDraft, setRoleDraft] = useState<Array<{ role: string; perPerson: number }>>([]);
  const save = useMutation({
    mutationFn: async (roles: any[]) => api.put('/v1/inventory/staffing-outlook', { roles }),
    onSuccess: () => { setEditing(false); qc.invalidateQueries({ queryKey: ['staffing-outlook'] }); },
  });
  if (!data?.days?.some((d: any) => d.expectedOrders > 0)) return null;
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <p className="text-xs uppercase tracking-wide text-slate-400">Staffing outlook · next 7 days</p>
        <button className="text-xs text-primary-600 font-medium" onClick={() => { setRoleDraft(data.roles); setEditing((e) => !e); }}>
          {editing ? 'Close' : 'Edit capacities'}
        </button>
      </div>
      <p className="text-xs text-slate-400 mb-3">Expected orders ÷ what one person handles per day — your numbers, editable. A guide, not a schedule.</p>
      {editing && (
        <div className="mb-3 space-y-1.5">
          {roleDraft.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="input input-sm w-40" value={r.role} onChange={(e) => setRoleDraft(roleDraft.map((x, j) => j === i ? { ...x, role: e.target.value } : x))} />
              <input type="number" min={1} className="input input-sm w-24 text-right" value={r.perPerson} onChange={(e) => setRoleDraft(roleDraft.map((x, j) => j === i ? { ...x, perPerson: +e.target.value || 1 } : x))} />
              <span className="text-xs text-slate-500">orders/person/day</span>
              <button className="text-slate-300 hover:text-red-500" onClick={() => setRoleDraft(roleDraft.filter((_, j) => j !== i))}>✕</button>
            </div>
          ))}
          <div className="flex gap-2">
            <button className="btn btn-sm btn-ghost" onClick={() => setRoleDraft([...roleDraft, { role: 'Role', perPerson: 100 }])}>+ role</button>
            <button className="btn btn-sm btn-primary" disabled={save.isPending || !roleDraft.length} onClick={() => save.mutate(roleDraft)}>{save.isPending ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-400">
            <th className="py-1 pr-3 font-normal">Day</th>
            <th className="py-1 pr-3 font-normal text-right">Expected orders</th>
            {data.roles.map((r: any) => <th key={r.role} className="py-1 pr-3 font-normal text-right">{r.role}</th>)}
          </tr></thead>
          <tbody>
            {data.days.map((d: any) => (
              <tr key={d.date} className="border-t border-slate-50">
                <td className="py-1.5 pr-3 text-slate-700">{d.weekday.slice(0, 3)} <span className="text-xs text-slate-400">{new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span></td>
                <td className="py-1.5 pr-3 text-right tabular-nums text-slate-900">{d.expectedOrders || '—'}</td>
                {d.roles.map((r: any) => <td key={r.role} className="py-1.5 pr-3 text-right tabular-nums font-medium text-slate-900">{r.suggested ?? '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
